import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { API_BASE } from '../config';
import SpatialCanvas from '../components/SpatialCanvas';

const STATUS_COLOR = {
  queued:    'var(--blue)',
  running:   'var(--rust)',
  completed: 'var(--green)',
  failed:    'var(--red)',
};

export default function Dashboard() {
  const { user }    = useAuth();
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try {
        const token = await user.getIdToken();
        const { data } = await axios.get(`${API_BASE}/jobs/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setJobs(data.jobs || []);
      } catch(e) {
        setError(e.message);
      }
      setLoading(false);
    };
    fetch();
    const iv = setInterval(fetch, 10000);
    return () => clearInterval(iv);
  }, [user]);

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '52px 32px' }}>
      {/* Hero row */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:48, marginBottom:64 }}>
        <div style={{ flex:1, paddingTop:8 }}>
          <div style={{
            fontFamily:'IBM Plex Mono, monospace', fontSize:10,
            color:'var(--rust)', letterSpacing:3, marginBottom:8,
            textTransform:'uppercase', opacity:0.9,
          }}>AI 3D Reconstruction Platform</div>
          <div style={{
            fontFamily:'IBM Plex Mono, monospace', fontSize:9,
            color:'rgba(255,255,255,0.25)', letterSpacing:'0.1em',
            marginBottom:20, textTransform:'uppercase',
          }}>
            by{' '}
            <a href="https://blackvanilla.ai" target="_blank" rel="noreferrer"
              style={{color:'#C8F04A', textDecoration:'none', opacity:0.6}}>
              Black Vanilla Technologies
            </a>
          </div>
          <h1 style={{ fontSize:40, fontWeight:500, lineHeight:1.15, marginBottom:20 }}>
            Physical spaces,<br />
            <span style={{ color:'var(--rust)' }}>precisely understood.</span>
          </h1>
          <p style={{ color:'var(--muted)', fontSize:15, lineHeight:1.7, maxWidth:400, marginBottom:32 }}>
            Upload a walkthrough video. civil3d produces a metric 3D model,
            semantic labels, measurements, and an AI agent that understands your space.
          </p>
          <div style={{ display:'flex', gap:12 }}>
            <Link to="/upload" className="btn btn-primary">Upload Video</Link>
            <a href="#jobs" className="btn btn-ghost">View Scans</a>
          </div>
          <div style={{ display:'flex', gap:32, marginTop:48, paddingTop:32, borderTop:'1px solid var(--border)' }}>
            {[['3D twin','From any video'],['AI-powered','Surface labeling'],['Ask anything','Natural language']].map(([val,label]) => (
              <div key={label}>
                <div style={{ fontFamily:'IBM Plex Mono, monospace', fontSize:20, fontWeight:500, color:'var(--white)', marginBottom:4 }}>{val}</div>
                <div style={{ fontSize:12, color:'var(--muted)', fontFamily:'IBM Plex Mono, monospace', letterSpacing:1 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{
          flexShrink:0, background:'var(--surface)',
          border:'1px solid var(--border)', borderRadius:10,
          overflow:'hidden', position:'relative',
        }}>
          <SpatialCanvas width={340} height={230} />
          <div style={{
            position:'absolute', top:12, left:14,
            fontFamily:'IBM Plex Mono, monospace', fontSize:9,
            color:'var(--rust)', letterSpacing:2, textTransform:'uppercase', opacity:0.8,
          }}>Live Reconstruction</div>
        </div>
      </div>

      {/* Jobs list */}
      <div>
        <div style={{
          display:'flex', alignItems:'center',
          justifyContent:'space-between', marginBottom:16,
        }}>
          <div style={{
            fontFamily:'IBM Plex Mono, monospace', fontSize:10,
            color:'var(--muted)', letterSpacing:2, textTransform:'uppercase',
          }}>Your Scans</div>
          <Link to="/upload" className="btn btn-ghost btn-sm">+ New</Link>
        </div>

        <div style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:8, overflow:'hidden', maxHeight:480, overflowY:'auto',
        }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--muted)',
              fontFamily:'IBM Plex Mono, monospace', fontSize:12 }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--red)', fontSize:13 }}>
              {error}
            </div>
          ) : jobs.length === 0 ? (
            <div style={{ padding:'56px 32px', textAlign:'center', color:'var(--muted)', fontSize:14 }}>
              No scans yet.&nbsp;
              <Link to="/upload" style={{ color:'var(--rust)', textDecoration:'none' }}>
                Upload your first video →
              </Link>
            </div>
          ) : (
            jobs.map((job, i) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                style={{
                  display:'flex', alignItems:'center',
                  padding:'16px 24px', textDecoration:'none',
                  borderBottom: i < jobs.length-1 ? '1px solid var(--border)' : 'none',
                  transition:'background 0.12s', gap:16,
                }}
                onMouseEnter={e => e.currentTarget.style.background='var(--elevated)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
              >
                <div style={{
                  width:34, height:34, borderRadius:6,
                  background:'var(--faint)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:15, flexShrink:0,
                }}>▤</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, color:'var(--white)', fontWeight:400, marginBottom:3 }}>
                    {job.video_filename || 'Unnamed scan'}
                  </div>
                  <div style={{ fontSize:11, color:'var(--muted)', fontFamily:'IBM Plex Mono, monospace' }}>
                    {new Date(job.created_at).toLocaleDateString()} ·
                    step {job.current_step || 0} of 8
                  </div>
                </div>
                <div style={{
                  fontSize:11, fontFamily:'IBM Plex Mono, monospace',
                  color: STATUS_COLOR[job.status] || 'var(--muted)',
                  padding:'3px 10px', borderRadius:20,
                  border:`1px solid ${STATUS_COLOR[job.status] || 'var(--muted)'}`,
                  opacity:0.85,
                }}>{job.status}</div>
                <div style={{ color:'var(--border-hi)', fontSize:16 }}>›</div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
